import { Box, Button, Divider, Group, MultiSelect, Paper, SimpleGrid, Text } from '@mantine/core';
import { useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { AxiosCommonError } from '../../../../../../../shared/types';
import { handleError } from '../../../../../../ui/err/handleError';
import { showSuccessNotification } from '../../../../../../ui/notification';
import useGetColumnMapping from '../hooks/useGetColumnMapping';
import { buildSearchString } from '../../../utils/buildSearchString';
import { aiAgentApis } from '../../../../../../../shared/apis/ai-agent/apis';

export type MappingFormValues = Record<string, string[]>;

// 필드 그룹 정의
export const FIELD_GROUPS = [
  ['receipt_manager_name', 'receipt_dt', 'serial_number', 'lot_number', 'equipment_reg_dt', 'equipment_location'] as const,
  ['customer_name', 'customer_address', 'customer_manager', 'customer_phone'] as const,
  ['receipt_no', 'receipt_type', 'receipt_content', 'receipt_result', 'receipt_note'] as const,
  [
    'process_dt',
    'process_manager_name',
    'process_type',
    'charge_type',
    'error_code',
    'cause',
    'process_content',
    'process_note',
  ] as const,
] as const;

export const FIELD_GROUP_COLUMN_NAMES = FIELD_GROUPS.reduce<string[]>((acc, group) => acc.concat(group), []);
type TFieldGroupColumnName = (typeof FIELD_GROUPS)[number][number];
const FIELD_GROUPS_FOR_RENDER: ReadonlyArray<ReadonlyArray<TFieldGroupColumnName>> = FIELD_GROUPS;
const toBackendMappingValue = (mappingValue: string) => mappingValue.toUpperCase();

export default function ColumnSection(props: {
  fields: {
    id: number;
    name: string;
    description: string;
    fieldOrder: number;
  }[];
  prevStep: () => void;
}) {
  const { fields, prevStep } = props;
  const params = useParams();
  const { templateId, companyId } = params;
  const [searchParams] = useSearchParams();
  const companyName = searchParams.get('companyName') || '';
  const navigate = useNavigate();

  const { mutate: createAsCsTemplateMapping, isLoading: isCreatingMapping } = useMutation(
    aiAgentApis.template.createAsCsTemplateMapping,
  );
  const { columnInfoMap: templateColumnInfoMap } = useGetColumnMapping();

  const defaultValues = useMemo<MappingFormValues>(() => {
    return FIELD_GROUP_COLUMN_NAMES.reduce<MappingFormValues>((acc, columnName) => {
      acc[columnName] = [];
      return acc;
    }, {});
  }, []);

  const { control, handleSubmit } = useForm<MappingFormValues>({
    defaultValues,
  });

  const transformToMappingFormat = (
    data: MappingFormValues,
  ): {
    mapping: Array<{
      fieldId: number;
      mappingOrder: number;
      mappingValue: string;
    }>;
  } => {
    const mapping: Array<{
      fieldId: number;
      mappingOrder: number;
      mappingValue: string;
    }> = [];

    // 각 필드명(키)에 대해 선택된 fieldId 배열을 순회
    Object.entries(data).forEach(([mappingValue, fieldIds]) => {
      if (fieldIds && fieldIds.length > 0) {
        // 각 필드명별로 mappingOrder는 1부터 시작
        fieldIds.forEach((fieldIdStr, index) => {
          const fieldId = Number(fieldIdStr);
          if (!isNaN(fieldId)) {
            mapping.push({
              fieldId,
              mappingOrder: index + 1, // 각 필드명 내에서의 순서 (1부터 시작)
              mappingValue: toBackendMappingValue(mappingValue),
            });
          }
        });
      }
    });

    return { mapping };
  };

  const onSubmit = (data: MappingFormValues) => {
    if (isCreatingMapping) {
      return;
    }

    const transformedData = transformToMappingFormat(data);
    createAsCsTemplateMapping(
      {
        templateId: Number(templateId),
        companyId: Number(companyId),
        mapping: transformedData.mapping,
      },
      {
        onSuccess: () => {
          showSuccessNotification('템플릿 맵핑 설정이 완료되었습니다.');
          navigate(
            `/admin/management/aiagent/company/${companyId}/template${buildSearchString({
              companyName,
            })}`,
          );
        },
        onError: (error) => {
          handleError(error as AxiosError<AxiosCommonError>);
        },
      },
    );
  };

  return (
    <Box>
      <Text fw={500}>컬럼 맵핑 설정</Text>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Paper withBorder p="md" my="md">
          {FIELD_GROUPS_FOR_RENDER.map((group, groupIndex) => (
            <div key={groupIndex}>
              {groupIndex > 0 && <Divider my="lg" />}
              <SimpleGrid cols={4} {...(groupIndex === 1 ? { my: 'lg' } : {})}>
                {group
                  .filter((columnName: TFieldGroupColumnName) => Boolean(templateColumnInfoMap[columnName]))
                  .map((columnName: TFieldGroupColumnName) => (
                    <Group key={columnName} gap="sm" align="center">
                      <Text size="sm">{templateColumnInfoMap[columnName].name}</Text>
                      <Controller
                        name={columnName}
                        control={control}
                        render={({ field }) => (
                          <MultiSelect
                            {...field}
                            data={fields?.map((field) => ({
                              value: field.id.toString(),
                              label: field.name,
                            }))}
                            style={{ flex: 1 }}
                          />
                        )}
                      />
                    </Group>
                  ))}
              </SimpleGrid>
            </div>
          ))}
        </Paper>
        <Group justify="center" mt="xl">
          <Button variant="default" onClick={prevStep}>
            이전
          </Button>
          <Button type="submit" disabled={isCreatingMapping}>
            저장
          </Button>
        </Group>
      </form>
    </Box>
  );
}
